// Route intelligence for Southern/Central African mining-export corridors.
//
// There is only a small, well-known set of corridors out of the DRC/Zambia
// Copperbelt to the regional ports, each with established border crossings. This
// curated knowledge base suggests the ordered border steps for a given
// origin→destination; the admin then confirms which crossing applies per step
// (e.g. Exit DR Congo → Kasumbalesa / Mokambo / Sakania). Coordinates are
// approximate — good enough for map markers.

export interface BorderCrossing {
  id: string
  name: string
  from: string
  to: string
  lat: number
  lng: number
}

export interface CorridorStep {
  /** e.g. "Exit DR Congo → Zambia". */
  label: string
  /** Candidate crossings for this step; the first is the common default. */
  options: BorderCrossing[]
}

export interface CorridorSuggestion {
  id: string
  /** e.g. "Copperbelt → Durban (via Zimbabwe)". */
  name: string
  steps: CorridorStep[]
}

const CROSSINGS = {
  kasumbalesa: {
    id: 'kasumbalesa',
    name: 'Kasumbalesa',
    from: 'DR Congo',
    to: 'Zambia',
    lat: -12.2508,
    lng: 27.7942,
  },
  mokambo: {
    id: 'mokambo',
    name: 'Mokambo',
    from: 'DR Congo',
    to: 'Zambia',
    lat: -12.4167,
    lng: 28.25,
  },
  sakania: {
    id: 'sakania',
    name: 'Sakania',
    from: 'DR Congo',
    to: 'Zambia',
    lat: -12.75,
    lng: 28.5333,
  },
  kipushi: {
    id: 'kipushi',
    name: 'Kipushi',
    from: 'DR Congo',
    to: 'Zambia',
    lat: -11.76,
    lng: 27.25,
  },
  nakonde: {
    id: 'nakonde',
    name: 'Nakonde / Tunduma',
    from: 'Zambia',
    to: 'Tanzania',
    lat: -9.3419,
    lng: 32.75,
  },
  chirundu: {
    id: 'chirundu',
    name: 'Chirundu',
    from: 'Zambia',
    to: 'Zimbabwe',
    lat: -16.0333,
    lng: 28.85,
  },
  victoriaFalls: {
    id: 'victoria-falls',
    name: 'Victoria Falls (Livingstone)',
    from: 'Zambia',
    to: 'Zimbabwe',
    lat: -17.9243,
    lng: 25.856,
  },
  kazungula: {
    id: 'kazungula',
    name: 'Kazungula Bridge',
    from: 'Zambia',
    to: 'Botswana',
    lat: -17.792,
    lng: 25.26,
  },
  katimaMulilo: {
    id: 'katima-mulilo',
    name: 'Katima Mulilo (Sesheke)',
    from: 'Zambia',
    to: 'Namibia',
    lat: -17.5,
    lng: 24.2833,
  },
  beitbridge: {
    id: 'beitbridge',
    name: 'Beitbridge',
    from: 'Zimbabwe',
    to: 'South Africa',
    lat: -22.2167,
    lng: 29.9833,
  },
  martinsDrift: {
    id: 'martins-drift',
    name: "Martin's Drift (Groblersbrug)",
    from: 'Botswana',
    to: 'South Africa',
    lat: -22.97,
    lng: 28.03,
  },
  forbesMachipanda: {
    id: 'forbes-machipanda',
    name: 'Forbes / Machipanda',
    from: 'Zimbabwe',
    to: 'Mozambique',
    lat: -18.92,
    lng: 32.85,
  },
} satisfies Record<string, BorderCrossing>

function normalize(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
}

export type Country =
  | 'DRC'
  | 'Zambia'
  | 'Tanzania'
  | 'SouthAfrica'
  | 'Zimbabwe'
  | 'Botswana'
  | 'Namibia'
  | 'Mozambique'
  | 'Malawi'

const COUNTRY_NAME: Record<Country, string> = {
  DRC: 'DR Congo',
  Zambia: 'Zambia',
  Tanzania: 'Tanzania',
  SouthAfrica: 'South Africa',
  Zimbabwe: 'Zimbabwe',
  Botswana: 'Botswana',
  Namibia: 'Namibia',
  Mozambique: 'Mozambique',
  Malawi: 'Malawi',
}

/** Best-effort country for a free-text location (Copperbelt region + regional ports). */
export function country(location: string): Country | null {
  const s = normalize(location)
  if (
    /(kolwezi|lubumbashi|likasi|kipushi|fungurume|tenke|kamoa|kambove|lualaba|katanga|\bdrc\b|\brdc\b|congo)/.test(
      s,
    )
  )
    return 'DRC'
  if (
    /(ndola|kitwe|chingola|mufulira|luanshya|solwezi|chililabombwe|kalulushi|lusaka|kabwe|livingstone|copperbelt|zambia)/.test(
      s,
    )
  )
    return 'Zambia'
  if (/(dar es salaam|dar-es-salaam|\bdar\b|dodoma|mbeya|tunduma|tanzania)/.test(s))
    return 'Tanzania'
  if (/(durban|johannesburg|richards bay|cape town|pretoria|south africa|\brsa\b)/.test(s))
    return 'SouthAfrica'
  if (/(harare|bulawayo|beitbridge|zimbabwe)/.test(s)) return 'Zimbabwe'
  if (/(gaborone|francistown|botswana)/.test(s)) return 'Botswana'
  if (/(walvis|windhoek|namibia)/.test(s)) return 'Namibia'
  if (/(beira|maputo|nacala|mozambique)/.test(s)) return 'Mozambique'
  if (/(lilongwe|blantyre|malawi)/.test(s)) return 'Malawi'
  return null
}

type OriginCountry = 'DRC' | 'Zambia'

/** Recognise the origin as being in the DRC or Zambian Copperbelt (for port corridors). */
export function originCountry(origin: string): OriginCountry | null {
  const c = country(origin)
  return c === 'DRC' || c === 'Zambia' ? c : null
}

// Border crossings between adjacent countries, looked up by unordered pair.
const ADJACENT: Record<string, BorderCrossing[]> = {
  'DRC|Zambia': [CROSSINGS.kasumbalesa, CROSSINGS.mokambo, CROSSINGS.sakania, CROSSINGS.kipushi],
  'Tanzania|Zambia': [CROSSINGS.nakonde],
  'Zambia|Zimbabwe': [CROSSINGS.chirundu, CROSSINGS.victoriaFalls],
  'Botswana|Zambia': [CROSSINGS.kazungula],
  'Namibia|Zambia': [CROSSINGS.katimaMulilo],
  'SouthAfrica|Zimbabwe': [CROSSINGS.beitbridge],
  'Botswana|SouthAfrica': [CROSSINGS.martinsDrift],
  'Mozambique|Zimbabwe': [CROSSINGS.forbesMachipanda],
}

function borderCrossings(a: Country, b: Country): BorderCrossing[] {
  return ADJACENT[[a, b].sort().join('|')] ?? []
}

type DestinationKey = 'dar' | 'durban' | 'walvis' | 'beira'

/** Recognise the destination port/city and its corridor. */
export function destinationKey(destination: string): DestinationKey | null {
  const d = normalize(destination)
  if (/(dar es salaam|dar-es-salaam|\bdar\b|tanzania)/.test(d)) return 'dar'
  if (/(durban|richards bay|johannesburg|south africa|\brsa\b)/.test(d)) return 'durban'
  if (/(walvis|namibia)/.test(d)) return 'walvis'
  if (/(beira|maputo|mozambique)/.test(d)) return 'beira'
  return null
}

/**
 * Suggest the border-crossing corridor(s) for a delivery. Returns [] unless the
 * origin is a recognised Copperbelt location AND the destination is a known
 * corridor endpoint. Durban has two variants (via Zimbabwe or via Botswana), so
 * more than one suggestion may be returned.
 */
export function suggestCorridors(
  origin: string | null | undefined,
  destination: string | null | undefined,
): CorridorSuggestion[] {
  if (!origin || !destination) {
    return []
  }
  const from = originCountry(origin)
  const to = destinationKey(destination)

  if (from && to) {
    const exitCongo: CorridorStep = {
      label: 'Exit DR Congo → Zambia',
      options: [CROSSINGS.kasumbalesa, CROSSINGS.mokambo, CROSSINGS.sakania, CROSSINGS.kipushi],
    }
    const base: CorridorStep[] = from === 'DRC' ? [exitCongo] : []

    switch (to) {
      case 'dar':
        return [
          {
            id: 'dar',
            name: 'Copperbelt → Dar es Salaam',
            steps: [...base, { label: 'Exit Zambia → Tanzania', options: [CROSSINGS.nakonde] }],
          },
        ]
      case 'walvis':
        return [
          {
            id: 'walvis',
            name: 'Copperbelt → Walvis Bay',
            steps: [...base, { label: 'Exit Zambia → Namibia', options: [CROSSINGS.katimaMulilo] }],
          },
        ]
      case 'beira':
        return [
          {
            id: 'beira',
            name: 'Copperbelt → Beira',
            steps: [
              ...base,
              { label: 'Exit Zambia → Zimbabwe', options: [CROSSINGS.chirundu] },
              { label: 'Exit Zimbabwe → Mozambique', options: [CROSSINGS.forbesMachipanda] },
            ],
          },
        ]
      case 'durban':
        return [
          {
            id: 'durban-zimbabwe',
            name: 'Copperbelt → Durban (via Zimbabwe)',
            steps: [
              ...base,
              {
                label: 'Exit Zambia → Zimbabwe',
                options: [CROSSINGS.chirundu, CROSSINGS.victoriaFalls],
              },
              { label: 'Exit Zimbabwe → South Africa', options: [CROSSINGS.beitbridge] },
            ],
          },
          {
            id: 'durban-botswana',
            name: 'Copperbelt → Durban (via Botswana)',
            steps: [
              ...base,
              { label: 'Exit Zambia → Botswana', options: [CROSSINGS.kazungula] },
              { label: 'Exit Botswana → South Africa', options: [CROSSINGS.martinsDrift] },
            ],
          },
        ]
    }
  }

  // Adjacent-country crossing (e.g. Lubumbashi DRC → Ndola Zambia).
  const originC = country(origin)
  const destC = country(destination)
  if (originC && destC && originC !== destC) {
    const crossings = borderCrossings(originC, destC)
    if (crossings.length > 0) {
      return [
        {
          id: `${originC}-${destC}`.toLowerCase(),
          name: `${COUNTRY_NAME[originC]} → ${COUNTRY_NAME[destC]}`,
          steps: [
            {
              label: `Exit ${COUNTRY_NAME[originC]} → ${COUNTRY_NAME[destC]}`,
              options: crossings,
            },
          ],
        },
      ]
    }
  }

  return []
}
